import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import HsnChooser, { HsnListButton, useHsnCodes } from '../components/HsnChooser';

/* ─────────────────────────────── SAP‑style css helpers ────────────────── */
const inp = 'w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue';
const sel = inp + " appearance-none bg-no-repeat bg-[length:10px_10px] bg-[position:right_6px_center] pr-5"
  + " bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20d%3D%22M0%200l5%206%205-6z%22%20fill%3D%22%23555%22%2F%3E%3C%2Fsvg%3E')]";
const chk = 'accent-sap-blue w-3.5 h-3.5';

/* ─────────────── YellowArrow (SAP B1 style) ─────────────── */
const YellowArrow = ({ onClick, title, disabled = false }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    title={title}
    disabled={disabled}
    className={`flex-shrink-0 flex items-center justify-center w-5 h-5 transition-opacity ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80'}`}
  >
    <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
    </svg>
  </button>
);

/* ─────────────────────────────── constants ─────────────────────────────── */
const TABS = [
  'General', 'Purchasing Data', 'Sales Data', 'Inventory Data',
  'Planning Data', 'Production Data', 'Properties', 'Remarks', 'Attachments',
];

/** SAP B1 uses an Item Type dropdown (e.g. Item / Labor / Travel / Fixed Assets by version). We map the common cases here. */
const ITEM_TYPES   = ['Goods', 'Service', 'Labour', 'Item', 'Resource'];
const CURRENCIES   = ['INR', 'USD', 'EUR', 'JPY', 'GBP'];
const MANAGE_BY    = ['None', 'Serial Numbers', 'Batches'];
const SHIP_TYPES   = ['Regular', 'Express', 'Freight'];
const GL_METHODS   = ['Item Group', 'Item Level', 'Warehouse'];
const VALUATIONS   = ['Moving Average', 'Standard', 'FIFO'];
const PLAN_METHODS = ['None', 'MRP'];
const PROC_METHODS = ['Buy', 'Make'];
const ISSUE_METHODS = ['Backflush', 'Manual'];
const UOM_OPTIONS  = ['Each', 'Kg', 'Lb', 'Box', 'Pallet', 'Meter', 'Foot', 'Liter', 'Gallon'];

const blankItem = {
  item_no: '', description: '', foreign_name: '', item_type: 'Goods',
  item_group_id: null, uom_group_id: null, price_list_id: null,
  unit_price: '0.0000', currency: 'INR', bar_code: '',
  is_inventory_item: 1, is_sales_item: 1, is_purchase_item: 1,
  tax_liable: 1, no_discount: 0, manufacturer: '', additional_identifier: '',
  standard_item_id: '', commodity_classification: '', linked_to_resource: '',
  shipping_type: 'Regular', manage_item_by: 'None', active_from: '', active_to: '',
  status: 'Active',
  // purchasing
  purchase_uom: 'Each', items_per_purchase: 1, packaging_uom: '', qty_per_package: 1, customs_group: '',
  preferred_vendor: '', mfr_catalog_no: '',
  purchase_length: 0, purchase_width: 0, purchase_height: 0, purchase_volume: 0, purchase_weight: 0,
  purchase_factor1: 0, purchase_factor2: 0, purchase_factor3: 0, purchase_factor4: 0,
  purchase_tax_group: '', hsn_code: '',
  // sales
  sale_uom: 'Each', items_per_sale: 1, commission_pct: '0.00', commission_group: '', revenue_account: '',
  sales_packaging_uom: '', sales_qty_per_package: 1,
  sales_length: 0, sales_width: 0, sales_height: 0, sales_volume: 0, sales_weight: 0,
  sales_factor1: 0, sales_factor2: 0, sales_factor3: 0, sales_factor4: 0,
  sales_tax_group: '', create_qr_code_from: '',
  // inventory
  gl_method: 'Item Group', manage_inventory_by_warehouse: 0, uom_name: '', item_weight: '0.0000',
  valuation_method: 'Moving Average', item_cost: 0, required_qty: '0.0000',
  // planning
  planning_method: 'MRP', procurement_method: 'Buy',
  lead_time_days: 0, tolerance_days: 0, min_order_qty: '1.0000',
  order_interval: 0, order_multiple: 1, checking_rule: '',
  // production
  phantom_item: 0, issue_method: 'Backflush', production_std_cost: 0, include_prod_cost_rollup: 0,
  properties: {}, remarks: '',
};

const fmtQty = v => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/** Available = In Stock − Committed + Ordered (SAP-style); use when API row has no `available` column. */
const warehouseAvailable = (w) => {
  if (w == null) return 0;
  if (w.available != null && w.available !== '') return Number(w.available);
  return (Number(w.in_stock) || 0) - (Number(w.committed) || 0) + (Number(w.ordered) || 0);
};

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════════ */
export default function ItemMasterDetail() {
  const { itemNo } = useParams();
  const navigate    = useNavigate();
  const location    = useLocation();
  const [searchParams] = useSearchParams();
  const isNew       = itemNo === 'new';
  const fromOrderRec = location.state?.from === 'order-recommendations';

  const initialTab = (() => {
    const t = searchParams.get('tab');
    if (!t) return 0;
    const idx = TABS.findIndex(tab => tab.toLowerCase() === t.toLowerCase());
    return idx >= 0 ? idx : 0;
  })();

  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [form, setForm]             = useState({ ...blankItem });
  const [original, setOriginal]     = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [boms, setBoms]             = useState([]);
  const [activeTab, setActiveTab]   = useState(initialTab);
  const [loading, setLoading]       = useState(!isNew);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showToast, setShowToast]   = useState(false);
  const tabBarRef = useRef(null);

  const [itemGroups, setItemGroups] = useState([]);
  const [uomGroups, setUomGroups]   = useState([]);
  const [priceCache, setPriceCache] = useState({});
  const [customPriceLists, setCustomPriceLists] = useState([]);
  const [showAddPriceListModal, setShowAddPriceListModal] = useState(false);
  const [newPriceListName, setNewPriceListName] = useState('');
  const [newPriceListFile, setNewPriceListFile] = useState(null);
  const [addingPriceList, setAddingPriceList] = useState(false);
  const [showAddItemGroupModal, setShowAddItemGroupModal] = useState(false);
  const [newItemGroupName, setNewItemGroupName]           = useState('');
  const [addingItemGroup, setAddingItemGroup]             = useState(false);

  const [showAddUomGroupModal, setShowAddUomGroupModal]   = useState(false);
  const [newUomGroupName, setNewUomGroupName]             = useState('');
  const [newUomBaseUom, setNewUomBaseUom]                 = useState('');
  const [addingUomGroup, setAddingUomGroup]               = useState(false);

  const loadItemGroups = useCallback(async () => {
    try {
      const data = await api.get('/item-groups');
      setItemGroups(Array.isArray(data) ? data : []);
    } catch { setItemGroups([]); }
  }, []);

  const loadUomGroups = useCallback(async () => {
    try {
      const data = await api.get('/uom-groups');
      setUomGroups(Array.isArray(data) ? data : []);
    } catch { setUomGroups([]); }
  }, []);

  const loadCustomPriceLists = useCallback(async () => {
    try {
      const data = await api.get('/items/custom-price-lists');
      setCustomPriceLists(Array.isArray(data) ? data : []);
    } catch { setCustomPriceLists([]); }
  }, []);

  useEffect(() => {
    loadItemGroups();
    loadUomGroups();
    loadCustomPriceLists();
  }, [loadItemGroups, loadUomGroups, loadCustomPriceLists]);

  const handleAddItemGroupClick = () => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can add new Item Groups');
      return;
    }
    setNewItemGroupName('');
    setShowAddItemGroupModal(true);
  };

  const handleAddUomGroupClick = () => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can add new UoM Groups');
      return;
    }
    setNewUomGroupName('');
    setNewUomBaseUom('');
    setShowAddUomGroupModal(true);
  };

  const submitNewItemGroup = async () => {
    if (!newItemGroupName.trim()) return;
    setAddingItemGroup(true);
    setError('');
    try {
      const created = await api.post('/item-groups', { group_name: newItemGroupName.trim() });
      await loadItemGroups();
      set('item_group_id', created.group_id);
      setShowAddItemGroupModal(false);
      setSuccessMsg(`Item Group '${created.group_name}' created`);
      setShowToast(true);
    } catch (e) {
      setError(e.message || 'Failed to create Item Group');
    } finally {
      setAddingItemGroup(false);
    }
  };

  const submitNewUomGroup = async () => {
    if (!newUomGroupName.trim()) return;
    setAddingUomGroup(true);
    setError('');
    try {
      const created = await api.post('/uom-groups', {
        uom_group_name: newUomGroupName.trim(),
        base_uom: newUomBaseUom.trim() || newUomGroupName.trim()
      });
      await loadUomGroups();
      set('uom_group_id', created.uom_group_id);
      setShowAddUomGroupModal(false);
      setSuccessMsg(`UoM Group '${created.uom_group_name}' created`);
      setShowToast(true);
    } catch (e) {
      setError(e.message || 'Failed to create UoM Group');
    } finally {
      setAddingUomGroup(false);
    }
  };

  const handleAddPriceListClick = () => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can add new Price Lists');
      return;
    }
    setNewPriceListName('');
    setNewPriceListFile(null);
    setShowAddPriceListModal(true);
  };

  const submitNewPriceList = async () => {
    if (!newPriceListName.trim()) {
      setError('Price List Name is required');
      return;
    }
    if (!newPriceListFile) {
      setError('Excel file is required');
      return;
    }

    setAddingPriceList(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('price_list_name', newPriceListName.trim());
      formData.append('file', newPriceListFile);

      const res = await api.postForm('/items/custom-price-lists', formData);

      await loadCustomPriceLists();
      set('price_list_id', res.price_list_name);
      setShowAddPriceListModal(false);
      setSuccessMsg(`Price List '${res.price_list_name}' created with ${res.row_count} prices`);
      setShowToast(true);

      // Fetch and cache the price for the current item if it has an item_no
      if (form.item_no) {
        const priceRes = await api.get(`/items/custom-price-list-price?price_list_name=${encodeURIComponent(res.price_list_name)}&item_no=${encodeURIComponent(form.item_no)}`);
        const budgetPrice = (priceRes && priceRes.price !== null && priceRes.price !== undefined) ? priceRes.price : '';
        set('unit_price', budgetPrice);
        setPriceCache(prev => ({
          ...prev,
          [res.price_list_name]: budgetPrice
        }));
      } else {
        set('unit_price', '');
        setPriceCache(prev => ({
          ...prev,
          [res.price_list_name]: ''
        }));
      }
    } catch (e) {
      setError(e.message || 'Failed to create Price List');
    } finally {
      setAddingPriceList(false);
    }
  };

  const openBomForItem = useCallback(async () => {
    if (!form?._item_id) {
      setError('Save the item first to open its BOM');
      return;
    }

    try {
      // Prefer BOM already included with item payload (fast path)
      let bomId = boms?.[0]?.bom_id;

      // Fallback: resolve BOM from BOM list by product_item_id / product_no
      if (!bomId) {
        const all = await api.get('/bom');
        const match = (all || []).find(b =>
          Number(b.product_item_id) === Number(form._item_id) ||
          (b.product_no && b.product_no === form.item_no)
        );
        bomId = match?.bom_id;
      }

      if (!bomId) {
        setError(`No BOM found for item "${form.item_no}"`);
        return;
      }

      navigate('/production/bom', { state: { openBomId: bomId } });
    } catch (e) {
      setError(e.message || 'Failed to open BOM');
    }
  }, [form?._item_id, form.item_no, boms, navigate]);

  /* ── Load item ── */
  useEffect(() => {
    if (isNew) {
      setPriceCache({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await api.get(`/items/by-no/${encodeURIComponent(itemNo)}`);
        if (cancelled) return;
        const parsed = { ...blankItem };
        for (const k of Object.keys(blankItem)) {
          if (data[k] !== undefined && data[k] !== null) parsed[k] = data[k];
        }
        if (typeof parsed.properties === 'string') {
          try { parsed.properties = JSON.parse(parsed.properties); } catch { parsed.properties = {}; }
        }
        parsed._item_id = data.item_id;
        setForm(parsed);
        setOriginal(parsed);
        setWarehouses(data.warehouses || []);
        setAttachments(data.attachments || []);
        setBoms(data.boms || []);

        setPriceCache({
          [data.price_list_id || 'None']: data.unit_price
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [itemNo, isNew]);

  /* ── Field change ── */
  const set = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccessMsg('');
  }, []);

  const toggleCheckbox = useCallback((field) => {
    setForm(prev => ({ ...prev, [field]: prev[field] ? 0 : 1 }));
  }, []);

  /* ── Save ── */
  const handleSave = async () => {
    if (!form.item_no.trim())      return setError('Item No. is required');
    if (!form.description.trim())  return setError('Description is required');
    if (form.price_list_id === 'Price List 01') {
      const p = parseFloat(form.unit_price);
      if (isNaN(p) || p <= 0) {
        return setError('Unit Price is required and must be greater than 0 when Price List 01 is selected');
      }
    }
    setSaving(true);
    setError('');
    setSuccessMsg('');
    setShowToast(false);
    try {
      const payload = { ...form };
      delete payload._item_id;
      if (isNew) {
        const created = await api.post('/items', payload);
        setSuccessMsg('Item created successfully');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
        navigate(`/item-master/${created.item_no}`, { replace: true });
      } else {
        await api.put(`/items/${form._item_id}`, payload);
        setOriginal({ ...form });
        setSuccessMsg('Item updated successfully');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* ── Keyboard tab navigation ── */
  const handleTabKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); setActiveTab(p => (p + 1) % TABS.length); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); setActiveTab(p => (p - 1 + TABS.length) % TABS.length); }
  };

  const dirty = original && JSON.stringify(form) !== JSON.stringify(original);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sap-blue border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-xs text-sap-textLight">Loading item…</span>
      </div>
    );
  }

  const breadcrumb = isNew ? 'Items › New' : `Items › ${form.item_no}`;

  return (
    <div className="space-y-0 text-[11px]">
      {/* ═══ Success Toast Banner ═══ */}
      {showToast && successMsg && (
        <div className="bg-green-50 border border-green-300 rounded-lg mx-0 px-4 py-2.5 flex items-center justify-between shadow-sm animate-pulse-once">
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
              <circle cx="12" cy="12" r="10" fill="#22c55e" opacity="0.15"/>
              <path d="M9 12l2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="1.5"/>
            </svg>
            <span className="text-xs font-semibold text-green-800">{successMsg}</span>
          </div>
          <button onClick={() => setShowToast(false)} className="text-green-600 hover:text-green-800 text-sm font-bold px-1">×</button>
        </div>
      )}

      {/* ═══ Error Banner ═══ */}
      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg mx-0 px-4 py-2.5 flex items-center gap-2 shadow-sm">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <circle cx="12" cy="12" r="10" fill="#ef4444" opacity="0.15"/>
            <path d="M15 9l-6 6M9 9l6 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.5"/>
          </svg>
          <span className="text-xs font-semibold text-red-800">{error}</span>
        </div>
      )}

      {/* ═══ SAP Title bar ═══ */}
      <div className="bg-[#E8E4DC] border-b border-[#C4BFB6] px-3 py-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/inventory/items')}
            className="text-[11px] text-sap-blue hover:underline">← Items</button>
          {fromOrderRec && (
            <>
              <span className="text-[10px] text-[#999]">|</span>
              <button onClick={() => navigate(location.state?.run_id ? `/mrp/recommendations?run_id=${location.state.run_id}` : -1)}
                className="text-[11px] text-amber-600 hover:underline font-semibold">← Order Recommendations</button>
            </>
          )}
          <span className="text-[10px] text-[#999]">|</span>
          <span className="text-xs font-semibold text-[#333]">Item Master Data</span>
          <span className="text-[10px] text-[#666]">({breadcrumb})</span>
          {dirty && <span className="text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded">Modified</span>}
        </div>
      </div>

      {/* ═══ Persistent Header ═══ */}
      <div className="bg-[#F4F3EF] border-b border-[#D5D0C8] px-4 py-3">
        <div className="grid grid-cols-[1fr_1fr] gap-x-12 gap-y-1.5">
          {/* LEFT COLUMN */}
          <div className="space-y-1.5">
            <Row label="Item No.">
              <input value={form.item_no} onChange={e => set('item_no', e.target.value)} disabled={!isNew}
                className={`${inp} ${!isNew ? 'bg-gray-100' : ''}`} style={{ maxWidth: 200 }} />
            </Row>
            <Row label="Description">
              <input value={form.description} onChange={e => set('description', e.target.value)} className={inp} />
            </Row>
            <Row label="Foreign Name">
              <input value={form.foreign_name || ''} onChange={e => set('foreign_name', e.target.value)} className={inp} />
            </Row>
            <Row label="Item Type">
              <select value={form.item_type || 'Goods'} onChange={e => set('item_type', e.target.value)} className={sel} style={{ maxWidth: 200 }}>
                {ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Row>
            <Row label="Item Group">
              <select
                value={form.item_group_id || ''}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '__add_new__') {
                    handleAddItemGroupClick();
                  } else {
                    set('item_group_id', val ? Number(val) : null);
                  }
                }}
                className={sel}
                style={{ maxWidth: 200 }}
              >
                <option value="">— Select Item Group —</option>
                {itemGroups.map(g => (
                  <option key={g.group_id} value={g.group_id}>
                    {g.group_name}
                  </option>
                ))}
                <option value="__add_new__" className="font-semibold text-sap-blue">
                  + Add New Item Group
                </option>
              </select>
            </Row>
            <Row label="UoM Group">
              <select
                value={form.uom_group_id || ''}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '__add_new__') {
                    handleAddUomGroupClick();
                  } else {
                    set('uom_group_id', val ? Number(val) : null);
                  }
                }}
                className={sel}
                style={{ maxWidth: 200 }}
              >
                <option value="">— Select UoM Group —</option>
                {uomGroups.map(u => (
                  <option key={u.uom_group_id} value={u.uom_group_id}>
                    {u.uom_group_name}
                  </option>
                ))}
                <option value="__add_new__" className="font-semibold text-sap-blue">
                  + Add New UoM Group
                </option>
              </select>
            </Row>
            <Row label="Price List">
              <select
                value={form.price_list_id || ''}
                onChange={async (e) => {
                  const val = e.target.value;
                  if (val === '__add_new__') {
                    handleAddPriceListClick();
                    return;
                  }

                  set('price_list_id', val);
                  const cacheKey = val || 'None';
                  if (priceCache[cacheKey] !== undefined) {
                    set('unit_price', priceCache[cacheKey]);
                  } else if (val) {
                    // Check if it's a dynamic custom price list
                    const isCustom = customPriceLists.some(p => p.price_list_name === val) || val === 'Z budget price';
                    if (isCustom) {
                      try {
                        if (form.item_no) {
                          const res = await api.get(`/items/custom-price-list-price?price_list_name=${encodeURIComponent(val)}&item_no=${encodeURIComponent(form.item_no)}`);
                          const budgetPrice = (res && res.price !== null && res.price !== undefined)
                            ? res.price
                            : '';
                          set('unit_price', budgetPrice);
                          setPriceCache(prev => ({
                            ...prev,
                            [cacheKey]: budgetPrice
                          }));
                        } else {
                          set('unit_price', '');
                          setPriceCache(prev => ({
                            ...prev,
                            [cacheKey]: ''
                          }));
                        }
                      } catch (err) {
                        console.error("Error fetching custom price:", err);
                        set('unit_price', '');
                        setPriceCache(prev => ({
                          ...prev,
                          [cacheKey]: ''
                        }));
                      }
                    } else {
                      // Standard price list not cached yet
                      set('unit_price', '');
                      setPriceCache(prev => ({
                        ...prev,
                        [cacheKey]: ''
                      }));
                    }
                  } else {
                    set('unit_price', '');
                    setPriceCache(prev => ({
                      ...prev,
                      [cacheKey]: ''
                    }));
                  }
                }}
                className={sel}
                style={{ maxWidth: 260 }}
              >
                <option value="">— None —</option>
                <option value="Base Price">Base Price</option>
                <option value="Price List 01">Price List 01</option>
                {customPriceLists.filter(p => p.price_list_name !== 'Base Price' && p.price_list_name !== 'Price List 01').map(p => (
                  <option key={p.price_list_name} value={p.price_list_name}>
                    {p.price_list_name}
                  </option>
                ))}
                {isSuperAdmin && (
                  <option value="__add_new__" className="font-semibold text-sap-blue">
                    + Add New Price List
                  </option>
                )}
              </select>
            </Row>
          </div>
 
          {/* RIGHT COLUMN */}
          <div className="space-y-1.5">
            <div className="flex flex-col gap-1 pt-0.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_inventory_item} onChange={() => toggleCheckbox('is_inventory_item')} className={chk} />
                <span className="text-[11px] text-[#333]">Inventory Item</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_sales_item} onChange={() => toggleCheckbox('is_sales_item')} className={chk} />
                <span className="text-[11px] text-[#333]">Sales Item</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_purchase_item} onChange={() => toggleCheckbox('is_purchase_item')} className={chk} />
                <span className="text-[11px] text-[#333]">Purchasing Item</span>
              </label>
            </div>
            <Row label="Bar Code">
              <input value={form.bar_code || ''} onChange={e => set('bar_code', e.target.value)} className={inp} />
            </Row>
            <Row label="Unit Price">
              <input
                type="number"
                step="0.0001"
                value={form.unit_price}
                onChange={e => {
                  const val = e.target.value;
                  set('unit_price', val);
                  setPriceCache(prev => ({
                    ...prev,
                    [form.price_list_id || 'None']: val
                  }));
                }}
                className={inp}
                style={{ maxWidth: 160 }}
              />
            </Row>
            <Row label="Primary Currency">
              <select value={form.currency} onChange={e => set('currency', e.target.value)} className={sel} style={{ maxWidth: 140 }}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Row>
          </div>
        </div>
      </div>

      {/* ═══ Tab Panel ═══ */}
      <div className="bg-white border-b border-[#D5D0C8]">
        {/* Tab bar */}
        <div ref={tabBarRef}
          className="flex border-b border-[#C4BFB6] bg-[#E8E4DC] px-1 overflow-x-auto"
          role="tablist" onKeyDown={handleTabKeyDown}>
          {TABS.map((tab, i) => (
            <button key={tab} role="tab" tabIndex={activeTab === i ? 0 : -1}
              aria-selected={activeTab === i}
              onClick={() => setActiveTab(i)}
              className={`px-3 py-1.5 text-[11px] whitespace-nowrap border-t border-l border-r transition-colors
                focus:outline-none
                ${activeTab === i
                  ? 'bg-white text-[#333] font-semibold border-[#C4BFB6] -mb-px z-10'
                  : 'text-[#666] bg-[#D5D0C8] border-transparent hover:bg-[#DDD9D1]'}`}>
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-4 min-h-[300px]">
          {activeTab === 0 && <GeneralTab form={form} set={set} toggleCheckbox={toggleCheckbox} />}
          {activeTab === 1 && <PurchasingTab form={form} set={set} />}
          {activeTab === 2 && <SalesTab form={form} set={set} />}
          {activeTab === 3 && <InventoryTab form={form} set={set} warehouses={warehouses} setWarehouses={setWarehouses} itemId={form._item_id} />}
          {activeTab === 4 && <PlanningTab form={form} set={set} />}
          {activeTab === 5 && <ProductionTab form={form} set={set} toggleCheckbox={toggleCheckbox} boms={boms}
            canOpenBom={!isNew && !!form._item_id}
            onOpenBom={openBomForItem} />}
          {activeTab === 6 && <PropertiesTab form={form} set={set} />}
          {activeTab === 7 && <RemarksTab form={form} set={set} />}
          {activeTab === 8 && <AttachmentsTab attachments={attachments} />}
        </div>
      </div>

      {/* ═══ Footer ═══ */}
      <div className="bg-[#E8E4DC] border-t border-[#C4BFB6] px-4 py-2 flex items-center justify-between">
        <div className="text-[10px] text-[#888]">
          {isNew ? 'New item' : `Item ID: ${form._item_id}`}
          {form.status === 'Inactive' && <span className="ml-2 text-red-600 font-medium">[Inactive]</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/inventory/items')}
            className="px-4 py-1.5 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1] text-[#333]">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] bg-sap-blue text-white rounded
                       hover:bg-sap-blueDark disabled:opacity-50 border border-sap-blue font-medium">
            {saving && <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            {saving ? 'Saving…' : isNew ? 'Add' : 'Update'}
          </button>
        </div>
      </div>

      {/* ═══ Modal: Add Item Group ═══ */}
      {showAddItemGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddItemGroupModal(false)}>
          <div className="bg-white rounded-xl shadow-xl border border-sap-border w-[400px] p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-bold text-sap-text">Add New Item Group</h3>
              <button type="button" onClick={() => setShowAddItemGroupModal(false)} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-sap-text block">Group Name *</label>
              <input
                type="text"
                autoFocus
                value={newItemGroupName}
                onChange={e => setNewItemGroupName(e.target.value)}
                placeholder="e.g. BO & RM or MFG. PRODUCT"
                className="w-full h-8 px-3 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => setShowAddItemGroupModal(false)}
                className="px-3 py-1.5 text-xs text-sap-textLight border border-sap-border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitNewItemGroup}
                disabled={addingItemGroup || !newItemGroupName.trim()}
                className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-50"
              >
                {addingItemGroup ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Modal: Add UoM Group ═══ */}
      {showAddUomGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddUomGroupModal(false)}>
          <div className="bg-white rounded-xl shadow-xl border border-sap-border w-[400px] p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-bold text-sap-text">Add New UoM Group</h3>
              <button type="button" onClick={() => setShowAddUomGroupModal(false)} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-sap-text block">UoM Group Name *</label>
                <input
                  type="text"
                  autoFocus
                  value={newUomGroupName}
                  onChange={e => setNewUomGroupName(e.target.value)}
                  placeholder="e.g. Length or Box"
                  className="w-full h-8 px-3 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-sap-text block">Base UoM</label>
                <input
                  type="text"
                  value={newUomBaseUom}
                  onChange={e => setNewUomBaseUom(e.target.value)}
                  placeholder="e.g. Meter or Box (defaults to Group Name)"
                  className="w-full h-8 px-3 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => setShowAddUomGroupModal(false)}
                className="px-3 py-1.5 text-xs text-sap-textLight border border-sap-border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitNewUomGroup}
                disabled={addingUomGroup || !newUomGroupName.trim()}
                className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-50"
              >
                {addingUomGroup ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Modal: Add Price List ═══ */}
      {showAddPriceListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddPriceListModal(false)}>
          <div className="bg-white rounded-xl shadow-xl border border-sap-border w-[400px] p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-bold text-sap-text">Add New Price List</h3>
              <button type="button" onClick={() => setShowAddPriceListModal(false)} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-sap-text block">Price List Name *</label>
                <input
                  type="text"
                  autoFocus
                  value={newPriceListName}
                  onChange={e => setNewPriceListName(e.target.value)}
                  placeholder="e.g. Christmas Sale 2026"
                  className="w-full h-8 px-3 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-sap-text block">Excel File *</label>
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={e => setNewPriceListFile(e.target.files[0])}
                  className="w-full text-xs border border-sap-border rounded p-1 focus:outline-none focus:border-sap-blue mt-1"
                />
                <span className="text-[10px] text-gray-500 block mt-1">Excel file must contain "Item No.", "Item Description", and "Price" columns.</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => setShowAddPriceListModal(false)}
                className="px-3 py-1.5 text-xs text-sap-textLight border border-sap-border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitNewPriceList}
                disabled={addingPriceList || !newPriceListName.trim() || !newPriceListFile}
                className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-50"
              >
                {addingPriceList ? 'Creating & Uploading…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Shared Primitives
   ═══════════════════════════════════════════════════════════════════════════ */
function Row({ label, children, className = '' }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <label className="text-[11px] text-[#555] w-[140px] shrink-0 text-right">{label}</label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Section({ title }) {
  return (
    <div className="flex items-center gap-2 py-1 mt-2 mb-1 border-b border-[#D5D0C8]">
      <span className="text-[11px] font-semibold text-[#333]">{title}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: General
   ═══════════════════════════════════════════════════════════════════════════ */
function GeneralTab({ form, set, toggleCheckbox }) {
  return (
    <div className="grid grid-cols-2 gap-x-12">
      <div className="space-y-1.5">
        <Section title="General" />
        <label className="flex items-center gap-2 cursor-pointer py-0.5">
          <input type="checkbox" checked={!!form.no_discount} onChange={() => toggleCheckbox('no_discount')} className={chk} />
          <span className="text-[11px]">Do Not Apply Discount Groups</span>
        </label>
        <Row label="Manufacturer">
          <input value={form.manufacturer || ''} onChange={e => set('manufacturer', e.target.value)} className={inp} />
        </Row>
        <Row label="Additional Identifier">
          <input value={form.additional_identifier || ''} onChange={e => set('additional_identifier', e.target.value)} className={inp} />
        </Row>
        <Row label="Shipping Type">
          <select value={form.shipping_type || ''} onChange={e => set('shipping_type', e.target.value)} className={sel}>
            {SHIP_TYPES.map(s => <option key={s}>{s}</option>)}
          </select>
        </Row>

        <Section title="Serial and Batch Numbers" />
        <Row label="Manage Item By">
          <select value={form.manage_item_by} onChange={e => set('manage_item_by', e.target.value)} className={sel}>
            {MANAGE_BY.map(m => <option key={m}>{m}</option>)}
          </select>
        </Row>
      </div>

      <div className="space-y-1.5">
        <Section title="Status" />
        <div className="flex items-center gap-4 py-0.5">
          {['Active', 'Inactive'].map(s => (
            <label key={s} className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="status" value={s} checked={form.status === s} onChange={e => set('status', e.target.value)}
                className="accent-sap-blue w-3.5 h-3.5" />
              <span className="text-[11px]">{s}</span>
            </label>
          ))}
        </div>
        <Row label="Active From">
          <input type="date" value={form.active_from || ''} onChange={e => set('active_from', e.target.value)} className={inp} style={{ maxWidth: 160 }} />
        </Row>
        <Row label="Active To">
          <input type="date" value={form.active_to || ''} onChange={e => set('active_to', e.target.value)} className={inp} style={{ maxWidth: 160 }} />
        </Row>

        <Section title="Additional" />
        <Row label="Linked to Resource">
          <input value={form.linked_to_resource || ''} onChange={e => set('linked_to_resource', e.target.value)} className={inp} />
        </Row>
        <Row label="Std Item Identification">
          <input value={form.standard_item_id || ''} onChange={e => set('standard_item_id', e.target.value)} className={inp} />
        </Row>
        <Row label="Commodity Classification">
          <input value={form.commodity_classification || ''} onChange={e => set('commodity_classification', e.target.value)} className={inp} />
        </Row>

        <Section title="Item Category" />
        <HsnField value={form.hsn_code || ''} onChange={(v) => set('hsn_code', v)} />
      </div>
    </div>
  );
}

/**
 * The item's HSN code, chosen from SAP's list.
 *
 * SAP keeps the codes as chapter rows and an item points at one of them, so a code
 * typed freehand would mean nothing to SAP. The field therefore works the way SAP's
 * does: the code is shown, and the button beside it opens the same "List of India
 * Chapter ID" window of Chapter, Heading and Subheading to pick from.
 */
function HsnField({ value, onChange }) {
  const { codes, loadError } = useHsnCodes();
  const [open, setOpen] = React.useState(false);

  const known = codes.find((c) => String(c.hsn_code) === String(value));

  return (
    <>
      <Row label="HSN">
        <div className="flex items-center gap-1">
          <input
            value={value}
            readOnly
            onClick={() => setOpen(true)}
            placeholder="e.g. 85.15.2190"
            title="Click to choose from SAP's HSN list"
            className={inp + ' cursor-pointer bg-[#FFFFE0]'}
            style={{ maxWidth: 200 }}
          />
          <HsnListButton
            onClick={() => setOpen(true)}
            className="h-[28px] w-[28px] border border-sap-border rounded bg-white hover:bg-sap-bg"
          />
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              title="Clear the HSN"
              className="h-[28px] px-2 shrink-0 text-[11px] text-sap-textLight border border-sap-border
                rounded bg-white hover:bg-sap-bg"
            >
              Clear
            </button>
          )}
        </div>
      </Row>
      {known?.description && (
        <p className="text-[10px] text-sap-textLight pl-[calc(38%+8px)]">{known.description}</p>
      )}
      {loadError && (
        <p className="text-[10px] text-amber-700 pl-[calc(38%+8px)]">Could not load the HSN list.</p>
      )}
      {open && (
        <HsnChooser
          codes={codes}
          current={value}
          onPick={(code) => { onChange(code); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Purchasing Data
   ═══════════════════════════════════════════════════════════════════════════ */
function PurchasingTab({ form, set }) {
  return (
    <div className="grid grid-cols-2 gap-x-12">
      <div className="space-y-1.5">
        <Row label="Preferred Vendor">
          <input value={form.preferred_vendor || ''} onChange={e => set('preferred_vendor', e.target.value)} className={inp} />
        </Row>
        <Row label="Mfr Catalog No.">
          <input value={form.mfr_catalog_no || ''} onChange={e => set('mfr_catalog_no', e.target.value)} className={inp} />
        </Row>
        <Row label="Purchasing UoM Name">
          <select value={form.purchase_uom || 'Each'} onChange={e => set('purchase_uom', e.target.value)} className={sel}>
            {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
          </select>
        </Row>
        <Row label="Items per Purch. Unit">
          <input type="number" value={form.items_per_purchase} onChange={e => set('items_per_purchase', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Packaging UoM Name">
          <select value={form.packaging_uom || ''} onChange={e => set('packaging_uom', e.target.value)} className={sel}>
            <option value="">— None —</option>
            {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
          </select>
        </Row>
        <Row label="Qty per Package">
          <input type="number" value={form.qty_per_package} onChange={e => set('qty_per_package', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>

        <Section title="Dimensions" />
        <Row label="Length">
          <input type="number" step="0.0001" value={form.purchase_length} onChange={e => set('purchase_length', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Width">
          <input type="number" step="0.0001" value={form.purchase_width} onChange={e => set('purchase_width', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Height">
          <input type="number" step="0.0001" value={form.purchase_height} onChange={e => set('purchase_height', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Volume (cc)">
          <input type="number" step="0.0001" value={form.purchase_volume} onChange={e => set('purchase_volume', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Weight">
          <input type="number" step="0.0001" value={form.purchase_weight} onChange={e => set('purchase_weight', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
      </div>

      <div className="space-y-1.5">
        <Section title="Factors" />
        <Row label="Factor 1">
          <input type="number" step="0.0001" value={form.purchase_factor1} onChange={e => set('purchase_factor1', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Factor 2">
          <input type="number" step="0.0001" value={form.purchase_factor2} onChange={e => set('purchase_factor2', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Factor 3">
          <input type="number" step="0.0001" value={form.purchase_factor3} onChange={e => set('purchase_factor3', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Factor 4">
          <input type="number" step="0.0001" value={form.purchase_factor4} onChange={e => set('purchase_factor4', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>

        <Section title="Tax" />
        <Row label="Customs Group">
          <input value={form.customs_group || ''} onChange={e => set('customs_group', e.target.value)} className={inp} />
        </Row>
        <Row label="Tax Group">
          <input value={form.purchase_tax_group || ''} onChange={e => set('purchase_tax_group', e.target.value)} className={inp} />
        </Row>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Sales Data
   ═══════════════════════════════════════════════════════════════════════════ */
function SalesTab({ form, set }) {
  return (
    <div className="grid grid-cols-2 gap-x-12">
      <div className="space-y-1.5">
        <Section title="Tax" />
        <Row label="Tax Group">
          <input value={form.sales_tax_group || ''} onChange={e => set('sales_tax_group', e.target.value)} className={inp} />
        </Row>
        <Row label="Commission %">
          <input type="number" step="0.01" value={form.commission_pct} onChange={e => set('commission_pct', e.target.value)} className={inp} style={{ maxWidth: 100 }} />
        </Row>
        <Row label="Commission Group">
          <input value={form.commission_group || ''} onChange={e => set('commission_group', e.target.value)} className={inp} />
        </Row>
        <Row label="Revenue Account">
          <input value={form.revenue_account || ''} onChange={e => set('revenue_account', e.target.value)} className={inp} />
        </Row>

        <Section title="Sales Units" />
        <Row label="Sales UoM Name">
          <select value={form.sale_uom || 'Each'} onChange={e => set('sale_uom', e.target.value)} className={sel}>
            {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
          </select>
        </Row>
        <Row label="Items per Sales Unit">
          <input type="number" value={form.items_per_sale} onChange={e => set('items_per_sale', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Packaging UoM Name">
          <select value={form.sales_packaging_uom || ''} onChange={e => set('sales_packaging_uom', e.target.value)} className={sel}>
            <option value="">— None —</option>
            {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
          </select>
        </Row>
        <Row label="Qty per Package">
          <input type="number" value={form.sales_qty_per_package} onChange={e => set('sales_qty_per_package', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>

        <Section title="Dimensions" />
        <Row label="Length">
          <input type="number" step="0.0001" value={form.sales_length} onChange={e => set('sales_length', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Width">
          <input type="number" step="0.0001" value={form.sales_width} onChange={e => set('sales_width', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Height">
          <input type="number" step="0.0001" value={form.sales_height} onChange={e => set('sales_height', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Volume">
          <input type="number" step="0.0001" value={form.sales_volume} onChange={e => set('sales_volume', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Weight">
          <input type="number" step="0.0001" value={form.sales_weight} onChange={e => set('sales_weight', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
      </div>

      <div className="space-y-1.5">
        <Section title="Factors" />
        <Row label="Factor 1">
          <input type="number" step="0.0001" value={form.sales_factor1} onChange={e => set('sales_factor1', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Factor 2">
          <input type="number" step="0.0001" value={form.sales_factor2} onChange={e => set('sales_factor2', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Factor 3">
          <input type="number" step="0.0001" value={form.sales_factor3} onChange={e => set('sales_factor3', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Factor 4">
          <input type="number" step="0.0001" value={form.sales_factor4} onChange={e => set('sales_factor4', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>

        <Section title="QR Code" />
        <Row label="Create QR Code From">
          <input value={form.create_qr_code_from || ''} onChange={e => set('create_qr_code_from', e.target.value)} className={inp} />
        </Row>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Inventory Data
   ═══════════════════════════════════════════════════════════════════════════ */
function InventoryTab({ form, set, warehouses, setWarehouses, itemId }) {
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsMsg, setLimitsMsg] = useState('');

  const updateWhField = (idx, field, value) => {
    setWarehouses(prev => prev.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  };

  const saveLimits = async () => {
    if (!itemId) return;
    setSavingLimits(true);
    setLimitsMsg('');
    try {
      await api.put(`/items/${itemId}/warehouse-limits`, {
        warehouses: warehouses.map(w => ({
          warehouse_id: w.warehouse_id,
          min_qty: Number(w.min_qty) || 0,
          max_qty: Number(w.max_qty) || 0,
        })),
      });
      setLimitsMsg('Min/Max levels saved');
    } catch (e) {
      setLimitsMsg('Error: ' + e.message);
    } finally {
      setSavingLimits(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-x-12">
        <div className="space-y-1.5">
          <Row label="Set G/L Accounts By">
            <select value={form.gl_method} onChange={e => set('gl_method', e.target.value)} className={sel}>
              {GL_METHODS.map(g => <option key={g}>{g}</option>)}
            </select>
          </Row>
          <label className="flex items-center gap-2 cursor-pointer py-0.5 ml-[148px]">
            <input type="checkbox" checked={!!form.manage_inventory_by_warehouse} onChange={() => set('manage_inventory_by_warehouse', form.manage_inventory_by_warehouse ? 0 : 1)} className={chk} />
            <span className="text-[11px]">Manage Inventory by Warehouse</span>
          </label>

          <Section title="Inventory Level" />
          <Row label="Required (Purch. UoM)">
            <input type="number" step="0.0001" value={form.required_qty} onChange={e => set('required_qty', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
          </Row>
          <Row label="Minimum">
            <input type="number" step="0.0001" value={form.min_order_qty} onChange={e => set('min_order_qty', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
          </Row>
        </div>

        <div className="space-y-1.5">
          <Row label="UoM Name">
            <input value={form.uom_name || ''} onChange={e => set('uom_name', e.target.value)} className={inp} />
          </Row>
          <Row label="Weight">
            <input type="number" step="0.0001" value={form.item_weight} onChange={e => set('item_weight', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
          </Row>
          <Row label="Valuation Method">
            <select value={form.valuation_method} onChange={e => set('valuation_method', e.target.value)} className={sel}>
              {VALUATIONS.map(v => <option key={v}>{v}</option>)}
            </select>
          </Row>
          <Row label="Item Cost">
            <input type="number" step="0.0001" value={form.item_cost} onChange={e => set('item_cost', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
          </Row>
        </div>
      </div>

      {/* Warehouse grid */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div>
            <Section title="Warehouse Stock" />
            <p className="text-[10px] text-[#888] mt-0.5 max-w-3xl leading-snug">
              Per-warehouse quantities come from inventory movements (opening balances, goods receipts, issues, transfers).
              All zeros usually means no stock has been posted yet for this item—use Inventory → Inventory Posting List (or receipts/transfers) to build balances; this grid then shows each warehouse row from the item’s warehouse assignments.
            </p>
          </div>
          {warehouses.length > 0 && itemId && (
            <div className="flex items-center gap-2">
              {limitsMsg && <span className={`text-[10px] ${limitsMsg.startsWith('Error') ? 'text-red-600' : 'text-green-700'}`}>{limitsMsg}</span>}
              <button onClick={saveLimits} disabled={savingLimits}
                className="px-3 py-1 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1] disabled:opacity-50">
                {savingLimits ? 'Saving…' : 'Save Min/Max'}
              </button>
            </div>
          )}
        </div>
        {warehouses.length === 0 ? (
          <p className="text-[11px] text-[#888] italic py-4 text-center">
            No warehouse data. Assign this item to warehouses to see stock levels.
          </p>
        ) : (
          <div className="border border-[#C4BFB6] overflow-auto max-h-52">
            <table className="w-full text-[11px]">
              <thead className="bg-[#E8E4DC] sticky top-0">
                <tr>
                  {['#', 'Whse Code', 'Whse Name', 'In Stock', 'Committed', 'Ordered', 'Available', 'Locked', 'Min Level', 'Max Level'].map(h => (
                    <th key={h} className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {warehouses.map((w, i) => (
                  <tr key={w.iw_id || i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F4F3EF]'} hover:bg-blue-50`}>
                    <td className="px-2 py-0.5 text-center text-[#888]">{i + 1}</td>
                    <td className="px-2 py-0.5 font-medium text-sap-blue">{w.wh_code}</td>
                    <td className="px-2 py-0.5">{w.wh_name}</td>
                    <td className="px-2 py-0.5 text-right">{fmtQty(w.in_stock)}</td>
                    <td className="px-2 py-0.5 text-right">{fmtQty(w.committed)}</td>
                    <td className="px-2 py-0.5 text-right">{fmtQty(w.ordered)}</td>
                    <td className="px-2 py-0.5 text-right font-semibold">{fmtQty(warehouseAvailable(w))}</td>
                    <td className="px-2 py-0.5 text-center">{w.is_locked ? '🔒' : ''}</td>
                    <td className="px-2 py-0.5">
                      <input type="number" min="0" step="1" value={w.min_qty ?? ''}
                        onChange={e => updateWhField(i, 'min_qty', e.target.value)}
                        className="w-16 px-1 py-0.5 text-right border border-[#C4BFB6] rounded text-[11px] focus:border-sap-blue focus:outline-none" />
                    </td>
                    <td className="px-2 py-0.5">
                      <input type="number" min="0" step="1" value={w.max_qty ?? ''}
                        onChange={e => updateWhField(i, 'max_qty', e.target.value)}
                        className="w-16 px-1 py-0.5 text-right border border-[#C4BFB6] rounded text-[11px] focus:border-sap-blue focus:outline-none" />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#E8E4DC] font-semibold">
                <tr>
                  <td className="px-2 py-1" colSpan={3}>Totals</td>
                  <td className="px-2 py-1 text-right">{fmtQty(warehouses.reduce((s, w) => s + Number(w.in_stock || 0), 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtQty(warehouses.reduce((s, w) => s + Number(w.committed || 0), 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtQty(warehouses.reduce((s, w) => s + Number(w.ordered || 0), 0))}</td>
                  <td className="px-2 py-1 text-right">{fmtQty(warehouses.reduce((s, w) => s + warehouseAvailable(w), 0))}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Planning Data
   ═══════════════════════════════════════════════════════════════════════════ */
function PlanningTab({ form, set }) {
  return (
    <div className="grid grid-cols-2 gap-x-12">
      <div className="space-y-1.5">
        <Section title="Planning Method" />
        <Row label="Planning Method">
          <select value={form.planning_method} onChange={e => set('planning_method', e.target.value)} className={sel}>
            {PLAN_METHODS.map(p => <option key={p}>{p}</option>)}
          </select>
        </Row>
        <Row label="Procurement Method">
          <select value={form.procurement_method} onChange={e => set('procurement_method', e.target.value)} className={sel}>
            {PROC_METHODS.map(p => <option key={p}>{p}</option>)}
          </select>
        </Row>

        <Section title="Order Parameters" />
        <Row label="Order Interval">
          <input type="number" value={form.order_interval} onChange={e => set('order_interval', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Order Multiple">
          <input type="number" value={form.order_multiple} onChange={e => set('order_multiple', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Minimum Order Qty">
          <input type="number" step="0.0001" value={form.min_order_qty} onChange={e => set('min_order_qty', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Checking Rule">
          <input value={form.checking_rule || ''} onChange={e => set('checking_rule', e.target.value)} className={inp} />
        </Row>
      </div>

      <div className="space-y-1.5">
        <Section title="Lead Time" />
        <Row label="Lead Time (Days)">
          <input type="number" value={form.lead_time_days} onChange={e => set('lead_time_days', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
        <Row label="Tolerance Days">
          <input type="number" value={form.tolerance_days} onChange={e => set('tolerance_days', e.target.value)} className={inp} style={{ maxWidth: 120 }} />
        </Row>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Production Data (NEW)
   ═══════════════════════════════════════════════════════════════════════════ */
function ProductionTab({ form, set, toggleCheckbox, boms, onOpenBom, canOpenBom }) {
  const itemComponents = boms.filter(b => b.component_item_id).length;
  const bomType = boms.length > 0 ? (boms[0].bom_type || 'Assembly') : '—';
  const hasBomHint = boms.length > 0 && boms[0].bom_id;

  return (
    <div className="grid grid-cols-2 gap-x-12">
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 cursor-pointer py-0.5">
          <input type="checkbox" checked={!!form.phantom_item} onChange={() => toggleCheckbox('phantom_item')} className={chk} />
          <span className="text-[11px]">Phantom Item</span>
        </label>
        <Row label="Issue Method">
          <select value={form.issue_method || 'Backflush'} onChange={e => set('issue_method', e.target.value)} className={sel}>
            {ISSUE_METHODS.map(m => <option key={m}>{m}</option>)}
          </select>
        </Row>
        <Row label="BOM Type">
          <div className="flex items-center gap-1">
            <input value={bomType} disabled className={`${inp} bg-gray-100`} />
            <YellowArrow
              title={!canOpenBom ? 'Save the item to open BOM' : (hasBomHint ? 'Open BOM' : 'Open BOM')}
              onClick={onOpenBom}
              disabled={!canOpenBom}
            />
          </div>
        </Row>
        <Row label="No. of Item Components">
          <input value={itemComponents} disabled className={`${inp} bg-gray-100`} style={{ maxWidth: 80 }} />
        </Row>
      </div>

      <div className="space-y-1.5">
        <Row label="Production Std Cost">
          <input type="number" step="0.0001" value={form.production_std_cost} onChange={e => set('production_std_cost', e.target.value)} className={inp} style={{ maxWidth: 140 }} />
        </Row>
        <label className="flex items-center gap-2 cursor-pointer py-0.5 ml-[148px]">
          <input type="checkbox" checked={!!form.include_prod_cost_rollup} onChange={() => toggleCheckbox('include_prod_cost_rollup')} className={chk} />
          <span className="text-[11px]">Include in Prod. Std Cost Rollup</span>
        </label>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Properties
   ═══════════════════════════════════════════════════════════════════════════ */
function PropertiesTab({ form, set }) {
  const props = form.properties && typeof form.properties === 'object' ? form.properties : {};
  const entries = Object.entries(props);

  const update = (newProps) => set('properties', newProps);

  const addRow = () => {
    const key = `Property ${entries.length + 1}`;
    update({ ...props, [key]: '' });
  };

  const removeRow = (key) => {
    const next = { ...props };
    delete next[key];
    update(next);
  };

  const renameKey = (oldKey, newKey) => {
    if (newKey === oldKey || !newKey.trim()) return;
    const next = {};
    for (const [k, v] of Object.entries(props)) {
      next[k === oldKey ? newKey : k] = v;
    }
    update(next);
  };

  const setValue = (key, value) => update({ ...props, [key]: value });

  const selectAll = () => {
    const next = {};
    for (const [k] of entries) next[k] = '✓';
    update(next);
  };

  const clearAll = () => {
    const next = {};
    for (const [k] of entries) next[k] = '';
    update(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button onClick={addRow}
          className="px-3 py-1 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1]">
          + Add Property
        </button>
        {entries.length > 0 && (
          <>
            <button onClick={selectAll}
              className="px-3 py-1 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1]">
              Select All
            </button>
            <button onClick={clearAll}
              className="px-3 py-1 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1]">
              Clear Selection
            </button>
          </>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="text-[11px] text-[#888] italic py-6 text-center">No properties defined.</p>
      ) : (
        <div className="border border-[#C4BFB6] overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-[#E8E4DC]">
              <tr>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6] w-10">#</th>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6]">Property Name</th>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6]">Value</th>
                <th className="px-2 py-1 border-b border-[#C4BFB6] w-8" />
              </tr>
            </thead>
            <tbody>
              {entries.map(([key, val], i) => (
                <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F4F3EF]'} hover:bg-blue-50`}>
                  <td className="px-2 py-0.5 text-center text-[#888]">{i + 1}</td>
                  <td className="px-2 py-0.5">
                    <input value={key} onChange={e => renameKey(key, e.target.value)}
                      className="w-full h-6 px-1.5 text-[11px] border border-[#C4BFB6] rounded focus:outline-none focus:border-sap-blue" />
                  </td>
                  <td className="px-2 py-0.5">
                    <input value={val} onChange={e => setValue(key, e.target.value)}
                      className="w-full h-6 px-1.5 text-[11px] border border-[#C4BFB6] rounded focus:outline-none focus:border-sap-blue" />
                  </td>
                  <td className="px-2 py-0.5 text-center">
                    <button onClick={() => removeRow(key)} className="text-red-400 hover:text-red-600" title="Remove">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Remarks
   ═══════════════════════════════════════════════════════════════════════════ */
function RemarksTab({ form, set }) {
  return (
    <div>
      <textarea
        value={form.remarks || ''}
        onChange={e => set('remarks', e.target.value)}
        rows={12}
        placeholder="Enter remarks, notes, or free-form text about this item…"
        className="w-full px-3 py-2 text-[11px] border border-[#C4BFB6] rounded resize-y
                   focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tab: Attachments
   ═══════════════════════════════════════════════════════════════════════════ */
function AttachmentsTab({ attachments }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button className="px-3 py-1 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1] opacity-50 cursor-not-allowed"
          disabled title="File upload requires server-side multipart support">
          Browse
        </button>
        <button className="px-3 py-1 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1] opacity-50 cursor-not-allowed" disabled>
          Display
        </button>
        <button className="px-3 py-1 text-[11px] bg-[#E8E4DC] border border-[#A09A90] rounded hover:bg-[#DDD9D1] opacity-50 cursor-not-allowed" disabled>
          Delete
        </button>
      </div>

      {attachments.length === 0 ? (
        <p className="text-[11px] text-[#888] italic py-6 text-center">No attachments.</p>
      ) : (
        <div className="border border-[#C4BFB6] overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-[#E8E4DC]">
              <tr>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6] w-10">#</th>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6]">Target Path</th>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6]">Type</th>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6]">Size</th>
                <th className="px-2 py-1 text-left font-medium text-[#333] border-b border-[#C4BFB6]">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((a, i) => (
                <tr key={a.attachment_id || i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F4F3EF]'} hover:bg-blue-50`}>
                  <td className="px-2 py-0.5 text-center text-[#888]">{i + 1}</td>
                  <td className="px-2 py-0.5 text-sap-blue">{a.file_name}</td>
                  <td className="px-2 py-0.5">{a.file_type || '—'}</td>
                  <td className="px-2 py-0.5">{a.file_size ? `${(a.file_size / 1024).toFixed(1)} KB` : '—'}</td>
                  <td className="px-2 py-0.5">{a.uploaded_at ? new Date(a.uploaded_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
